import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Clock, AlertTriangle, User, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { approvalAPI, requestAPI } from '@/lib/api';
import { cn, formatDate, getStatusColor, getUrgencyBorder } from '@/lib/utils';
import { toast } from 'sonner';

const ApprovalCard = ({ approval, onApprove, onReject }) => {
  const urgencyColors = {
    low: 'bg-slate-100 text-slate-700',
    medium: 'bg-blue-100 text-blue-700',
    high: 'bg-orange-100 text-orange-700',
    critical: 'bg-red-100 text-red-700',
  };

  return (
    <Card 
      className={cn("border-l-4", getUrgencyBorder(approval.urgency))}
      data-testid={`approval-card-${approval.id}`}
    >
      <CardContent className="p-5">
        <div className="flex flex-col lg:flex-row lg:items-start gap-4">
          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-sm font-semibold text-slate-900">
                {approval.request_number}
              </span>
              <Badge className={cn("text-xs", urgencyColors[approval.urgency])}>
                {approval.urgency.toUpperCase()}
              </Badge>
              {approval.days_pending > 2 && (
                <Badge variant="destructive" className="text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {approval.days_pending}d overdue
                </Badge>
              )}
            </div>
            
            <h3 className="text-base font-medium text-slate-900 mb-2">
              {approval.part_description}
            </h3>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-slate-500">Quantity</p>
                <p className="font-medium">{approval.quantity}</p>
              </div>
              <div>
                <p className="text-slate-500">Requester</p>
                <p className="font-medium flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {approval.requester_name}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Branch</p>
                <p className="font-medium flex items-center gap-1">
                  <Building2 className="w-3 h-3" />
                  {approval.branch_name}
                </p>
              </div>
              <div>
                <p className="text-slate-500">Requested</p>
                <p className="font-medium">{formatDate(approval.created_at)}</p>
              </div>
            </div>

            {approval.reason && (
              <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Reason: </span>
                  {approval.reason}
                </p>
              </div>
            )}

            {approval.job_reference && (
              <p className="mt-2 text-sm text-slate-500">
                Job Reference: <span className="font-mono">{approval.job_reference}</span>
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex lg:flex-col gap-2 lg:w-32">
            <Button
              className="flex-1 lg:flex-none bg-emerald-600 hover:bg-emerald-700"
              onClick={() => onApprove(approval)}
              data-testid={`approve-btn-${approval.id}`}
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Approve
            </Button>
            <Button
              variant="outline"
              className="flex-1 lg:flex-none border-red-200 text-red-600 hover:bg-red-50"
              onClick={() => onReject(approval)}
              data-testid={`reject-btn-${approval.id}`}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function ApprovalsPage() {
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [rejectDialog, setRejectDialog] = useState({ open: false, approval: null });
  const [rejectReason, setRejectReason] = useState('');

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const response = await approvalAPI.getPending();
      setApprovals(response.data.approvals || []);
    } catch (error) {
      console.error('Failed to fetch approvals:', error);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApprovals();
  }, []);

  const handleApprove = async (approval) => {
    setProcessing(true);
    try {
      await requestAPI.approve(approval.id);
      toast.success(`Request ${approval.request_number} approved`);
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (approval) => {
    setRejectDialog({ open: true, approval });
    setRejectReason('');
  };

  const handleRejectConfirm = async () => {
    if (!rejectReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      await requestAPI.reject(rejectDialog.approval.id, rejectReason);
      toast.success(`Request ${rejectDialog.approval.request_number} rejected`);
      setRejectDialog({ open: false, approval: null });
      fetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const criticalCount = approvals.filter(a => a.urgency === 'critical').length;
  const highCount = approvals.filter(a => a.urgency === 'high').length;
  const overdueCount = approvals.filter(a => a.days_pending > 2).length;

  return (
    <div className="space-y-6" data-testid="approvals-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Approval Queue</h1>
        <p className="text-slate-600">{approvals.length} requests pending your approval</p>
      </div>

      {/* Summary Cards */}
      {approvals.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {criticalCount > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold text-red-700">{criticalCount}</p>
                  <p className="text-sm text-red-600">Critical Priority</p>
                </div>
              </CardContent>
            </Card>
          )}
          {highCount > 0 && (
            <Card className="border-orange-200 bg-orange-50">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold text-orange-700">{highCount}</p>
                  <p className="text-sm text-orange-600">High Priority</p>
                </div>
              </CardContent>
            </Card>
          )}
          {overdueCount > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4 flex items-center gap-3">
                <Clock className="w-8 h-8 text-amber-500" />
                <div>
                  <p className="text-2xl font-bold text-amber-700">{overdueCount}</p>
                  <p className="text-sm text-amber-600">Overdue (2+ days)</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Approval List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      ) : approvals.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle className="w-12 h-12 mx-auto text-emerald-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">All caught up!</h3>
            <p className="text-slate-500">
              No requests pending your approval
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {approvals.map((approval) => (
            <ApprovalCard
              key={approval.id}
              approval={approval}
              onApprove={handleApprove}
              onReject={handleRejectClick}
            />
          ))}
        </div>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, approval: rejectDialog.approval })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Request</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting {rejectDialog.approval?.request_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={4}
              data-testid="reject-reason-input"
            />
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setRejectDialog({ open: false, approval: null })}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleRejectConfirm}
                disabled={processing || !rejectReason.trim()}
                data-testid="confirm-reject-btn"
              >
                {processing ? 'Rejecting...' : 'Reject Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
